/* eslint-disable max-lines */
import { Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { Checkbox, CheckboxListFormik } from '@strata/react-checkbox-list';
import { DateInputFormik } from '@strata/react-date-time';
import { FormFieldWrapper } from '@strata/react-form-field-wrapper';
import { NumberInputFormik, TextAreaFormik, TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import { FieldArray, getIn, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import {
    addUniqueString,
    FieldTypes,
    getIsRequired,
    StopPropagation,
} from 'components/valueCase/helpers';
import {
    handleNumericValidation,
    yupNumber,
} from 'pages/valueCase/helpers/valueCaseValidationSchema';

addUniqueString();

const validationFunction = (useFieldLevelValidation, formik, fieldId, validationSchema) =>
    useFieldLevelValidation
        ? async (value) => {
              if (!validationSchema) {
                  return undefined;
              }
              const validCheck = await validationSchema
                  .validate(value)
                  .catch((err) => err.errors[0]);
              if (validCheck !== value) {
                  formik.setFieldError(fieldId, validCheck);
                  return validCheck;
              }
              return undefined;
          }
        : undefined;

const InputField = ({ formObject, fieldPath, useFieldLevelValidation = true }) => {
    const formik = useFormikContext();
    const fieldId = `${fieldPath}.${formObject.id}`;

    const baseProps = {
        id: fieldId,
        label: formObject.label,
        required: formObject.required,
        helperText: formObject.helperText,
        placeholderText: formObject.placeholderText,
    };

    switch (formObject.type) {
        case FieldTypes.TEXT:
            return (
                <TextInputFormik
                    {...baseProps}
                    validate={validationFunction(
                        useFieldLevelValidation,
                        formik,
                        fieldId,
                        getIsRequired(
                            Yup.string().max(200, validationStrings.maxCharacter.lambda(200)),
                            baseProps.required,
                        ),
                    )}
                />
            );
        case FieldTypes.TEXT_AREA:
            return (
                <TextAreaFormik
                    {...baseProps}
                    validate={validationFunction(
                        useFieldLevelValidation,
                        formik,
                        fieldId,
                        getIsRequired(
                            Yup.string().max(500, validationStrings.maxCharacter[500]),
                            baseProps.required,
                        ),
                    )}
                />
            );
        case FieldTypes.NUMBER:
            return (
                <NumberInputFormik
                    {...baseProps}
                    validate={validationFunction(
                        useFieldLevelValidation,
                        formik,
                        fieldId,
                        getIsRequired(
                            handleNumericValidation(formObject, yupNumber),
                            baseProps.required,
                        ),
                    )}
                />
            );
        case FieldTypes.DATE:
            return (
                <DateInputFormik
                    {...baseProps}
                    validate={validationFunction(
                        useFieldLevelValidation,
                        formik,
                        fieldId,
                        getIsRequired(Yup.date(), baseProps.required),
                    )}
                    requiredValidationMessage={
                        getIn(formik.errors, baseProps.id) && getIn(formik.touched, baseProps.id)
                            ? validationStrings.requiredField.errorMessage
                            : null
                    }
                    onChange={(newValue) =>
                        formik.setFieldValue(
                            baseProps.id,
                            formatDate(newValue, DateFormats.HYPHEN_DATE),
                        )
                    }
                    value={getIn(formik.values, baseProps.id)}
                />
            );
        case FieldTypes.CHECKBOX:
            return (
                <div className="padding__top--medium">
                    <FormFieldWrapper
                        label=""
                        helperText={baseProps.helperText}
                        inputContent={
                            <Checkbox
                                {...baseProps}
                                onChange={(newValue) =>
                                    formik.setFieldValue(baseProps.id, newValue)
                                }
                                checked={getIn(formik.values, baseProps.id) || false}
                            />
                        }
                    />
                </div>
            );
        case FieldTypes.SINGLE_SELECT:
            return (
                <SuggestSingleSelectFormik
                    {...baseProps}
                    values={formObject.options}
                    validate={validationFunction(
                        useFieldLevelValidation,
                        formik,
                        fieldId,
                        getIsRequired(Yup.string(), baseProps.required),
                    )}
                />
            );
        case FieldTypes.SINGLE_SELECT_OPTIONS:
            return (
                <div className="sds_dynamic-field__options">
                    <FormFieldWrapper
                        label={baseProps.label}
                        helperText={baseProps.helperText}
                        inputContent={
                            <FieldArray name={baseProps.id}>
                                {(arrayHelpers) => {
                                    const optionsCount =
                                        getIn(formik.values, baseProps.id)?.length ?? 0;
                                    const showDeleteOptionButtons = optionsCount > 2;
                                    const optionFieldSchema = getIsRequired(
                                        Yup.string().max(200, validationStrings.maxCharacter[200]),
                                        true,
                                    ).unique('Option must be unique.');
                                    return (
                                        <>
                                            {(getIn(formik.values, baseProps.id) ?? []).map(
                                                (_option, index) => (
                                                    <div
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        key={index}
                                                        className="sds_dynamic-field__option"
                                                    >
                                                        <div className="sds_dynamic-field__option__text">
                                                            <TextInputFormik
                                                                id={`${arrayHelpers.name}.${index}`}
                                                                label={`Option ${index + 1}`}
                                                                required
                                                                validate={validationFunction(
                                                                    useFieldLevelValidation,
                                                                    formik,
                                                                    fieldId,
                                                                    optionFieldSchema,
                                                                )}
                                                            />
                                                        </div>
                                                        {showDeleteOptionButtons && (
                                                            <Icon
                                                                title="Delete"
                                                                icon={IconNames.CROSS}
                                                                onClick={() => {
                                                                    arrayHelpers.remove(index);
                                                                }}
                                                                className="sds_dynamic-field__option__delete-button"
                                                            />
                                                        )}
                                                    </div>
                                                ),
                                            )}
                                            <div className="sds_dynamic-field__add-option-button">
                                                <Button
                                                    label="Add Option"
                                                    onClick={() => {
                                                        arrayHelpers.push('');
                                                        formik.validateField(
                                                            `${arrayHelpers.name}.${optionsCount}`,
                                                        );
                                                    }}
                                                />
                                            </div>
                                        </>
                                    );
                                }}
                            </FieldArray>
                        }
                    />
                </div>
            );
        case FieldTypes.RADIO_BUTTONS:
            return (
                <RadioButtonGroupFormik
                    name={baseProps.id}
                    {...baseProps}
                    options={[
                        ...new Set((formObject.options ?? []).filter((option) => option !== '')),
                    ]}
                    validate={validationFunction(
                        useFieldLevelValidation,
                        formik,
                        fieldId,
                        getIsRequired(Yup.string(), baseProps.required),
                    )}
                />
            );
        case FieldTypes.CHECKBOX_LIST:
            return (
                <CheckboxListFormik
                    name={baseProps.id}
                    {...baseProps}
                    options={[
                        ...new Set((formObject.options ?? []).filter((option) => option !== '')),
                    ]}
                    validate={validationFunction(
                        useFieldLevelValidation,
                        formik,
                        fieldId,
                        getIsRequired(Yup.array().of(Yup.string()), baseProps.required, [
                            ['min', 1, validationStrings.requiredField.errorMessage],
                        ]),
                    )}
                />
            );
        case FieldTypes.MULTI_SELECT:
            return (
                <SuggestMultiSelectFormik
                    name={baseProps.id}
                    {...baseProps}
                    values={formObject.options}
                />
            );
        default:
            return null;
    }
};

InputField.propTypes = {
    formObject: PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.oneOf(Object.values(FieldTypes)),
        label: PropTypes.string,
        required: PropTypes.bool,
        default: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
            PropTypes.instanceOf(Date),
            PropTypes.arrayOf(PropTypes.string),
        ]),
        options: PropTypes.arrayOf(PropTypes.string),
        helperText: PropTypes.string,
        placeholderText: PropTypes.string,
        numericValidation: PropTypes.shape({
            min: PropTypes.number,
            max: PropTypes.number,
            isInteger: PropTypes.bool,
        }),
    }).isRequired,
    fieldPath: PropTypes.string.isRequired,
    useFieldLevelValidation: PropTypes.bool,
};

const defaultValidationMap = {
    number: PropTypes.number,
    checkbox: PropTypes.bool,
    date: PropTypes.instanceOf(Date),
};

const mapConfigurableParamsToForm = (configurableParams) =>
    Object.keys(configurableParams).map((param) => ({
        id: param,
        ...configurableParams[param],
    }));

export const DynamicFields = ({ configurableParams = {}, fieldPath }) => {
    const formObjects =
        Object.keys(configurableParams).length !== 0
            ? mapConfigurableParamsToForm(configurableParams)
            : [];

    return (
        <div className="sds_dynamic-field__container" {...StopPropagation}>
            {formObjects.map((formObject) => (
                <div key={formObject.id} className="sds_dynamic-field__field">
                    <InputField formObject={formObject} fieldPath={fieldPath} />
                </div>
            ))}
        </div>
    );
};

DynamicFields.propTypes = {
    configurableParams: PropTypes.objectOf(
        PropTypes.exact({
            type: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            required: PropTypes.bool,
            helperText: PropTypes.string,
            placeholderText: PropTypes.string,
            default: (props) => {
                PropTypes.checkPropTypes(
                    { default: defaultValidationMap[props.type] ?? PropTypes.string },
                    { default: props.default },
                    'prop',
                    'DynamicFields',
                );
                if (props.default && props.type === FieldTypes.SINGLE_SELECT) {
                    if (!props.options?.includes(props.default)) {
                        return new Error(
                            'Invalid default value supplied in condition object. Default value must be included in options. Validation failed.',
                        );
                    }
                    return null;
                }
                return null;
            },
            options: PropTypes.arrayOf(PropTypes.string),
            numericValidation: PropTypes.shape({
                min: PropTypes.number,
                max: PropTypes.number,
                isInteger: PropTypes.bool,
            }),
        }).isRequired,
    ),
    fieldPath: PropTypes.string.isRequired,
};

DynamicFields.FieldTypes = FieldTypes;
DynamicFields.defaultValidationMap = defaultValidationMap;
